import React from "react"
import { Badge, Button, Card, CardBody } from "reactstrap"
import { actorName, logTime, sheetStatuses } from "./labels"
import { COURIER_UNCONFIGURED, abroad, activeAwbOf, asksResend, cancellationLabel, cargusLabelled, carriedItems, courierAlertHints, courierStatusLabel, dayLabel,
  emailColor, emailDeliveryLabel, messageName, resendable, returnLabel, shipmentTitle, shipmentWeightLabel, transportLabel } from "./shipmentLabels"
import GenerationState from "./GenerationState"
import { sheetShipped, shipmentEditable, shipmentReadyToShip, shipmentShipped } from "./sheetState"

// One action on a shipment, opening its dialog.
function Action({ kind, shipment, pending, onOpen, color = "light", disabled = false, children }) {
  return <Button color={color} size="sm" className="mr-2 mb-1" disabled={pending || disabled} onClick={() => onOpen(kind, shipment)}>{children}</Button>
}

// What can be done with a shipped shipment (US 84, 86–94): its AWB entered by hand, or its note, corrected; a return recorded or
// corrected; its cancellation. An AWB Cargus generated is never edited, nor the address or contact on it (Events refuses them): its
// shipment is cancelled instead, before the courier picks it up. A supplementary shipment also has its content corrected here, and its
// address and contact when its AWB was entered by hand; the main shipment's are the sheet's lines and delivery. On a canceled event's sheet
// (`readOnly`, D10), only its returns.
function ShippedActions({ shipment, readOnly, pending, onOpen }) {
  const generated = shipment.awbSource === "generated"
  const supplementary = shipment.kind === "supplementary"
  const action = (kind, label, icon) => <Action key={kind} kind={kind} shipment={shipment} pending={pending} onOpen={onOpen}>
    {icon && <i className={`bx ${icon} mr-1`} aria-hidden="true" />}{label}
  </Action>
  const returns = [action("return", "Record return", "bx-undo"), shipment.returns?.length > 0 && action("correctReturn", "Correct the return")]
  if (readOnly) return <div className="d-flex flex-wrap mt-2">{returns}</div>
  return <>
    <div className="d-flex flex-wrap mt-2">
      {supplementary && action("correctContent", "Correct the content")}
      {!generated && action("transport", shipment.transport === "noCourier" ? "Correct the note" : "Correct the AWB")}
      {supplementary && !generated && action("address", "Correct the address")}
      {supplementary && !generated && action("contact", "Correct the contact")}
      {returns}
      <Button color="danger" outline size="sm" className="mb-1" disabled={pending} onClick={() => onOpen("cancel", shipment)}>Cancel the shipment</Button>
    </div>
    {generated && <p className="small text-muted mt-1 mb-0">Cargus generated this AWB: to change it, or the address or contact on it, cancel the shipment
      before the courier picks it up.</p>}
  </>
}

// A supplementary shipment not shipped yet (US 89, 97, 98, D19), whatever its main shipment's state (Tudor, 2026-10-22). In preparation:
// its processing, and when its address is in Romania the generation of its AWB through Cargus, with how one that did not end stands,
// disabled on a server without a courier, and the AI prompt for it; its AWB changed once a processing undone stated a courier; its own
// address and contact, changed without a reason; its deletion. Processed: Mark as shipped, its AWB changed, its processing undone. On a
// canceled event's sheet (`readOnly`, D10), only the cancellation of the AWB it holds (Tudor, 2026-10-22).
function PreparingActions({ shipment, readOnly, hasCourier, pending, onOpen }) {
  const props = { shipment, pending, onOpen }
  if (readOnly) {
    return activeAwbOf(shipment) && <div className="d-flex flex-wrap mt-2"><Action kind="cancelAwb" {...props}>Cancel the AWB</Action></div>
  }
  const romanian = !abroad(shipment.address)
  const awbChanges = shipment.transport === "courier" && shipment.awbSource !== "generated"
  if (shipmentReadyToShip(shipment.status)) {
    return <div className="d-flex flex-wrap mt-2">
      <Action kind="ship" color="primary" {...props}><i className="bx bx-send mr-1" aria-hidden="true" />Mark as shipped</Action>
      {awbChanges && <Action kind="awb" {...props}>Change AWB</Action>}
      <Action kind="undo" {...props}>Undo processing</Action>
    </div>
  }
  return <>
    {shipment.generation && <div className="mt-2"><GenerationState generation={shipment.generation} pending={pending || !hasCourier}
      onRetry={() => onOpen("generate", shipment)} /></div>}
    <div className="d-flex flex-wrap mt-2">
      {romanian && <Action kind="generate" color="primary" disabled={!hasCourier} {...props}>
        <i className="bx bx-barcode mr-1" aria-hidden="true" />Generate AWB
      </Action>}
      <Action kind="process" color={romanian ? "light" : "primary"} {...props}>Validate processing</Action>
      {romanian && <Action kind="prompt" {...props}><i className="bx bx-bot mr-1" aria-hidden="true" />AI prompt</Action>}
      {awbChanges && <Action kind="awb" {...props}>{shipment.awb ? "Change AWB" : "Add AWB"}</Action>}
      <Action kind="address" {...props}>Change the address</Action>
      <Action kind="contact" {...props}>Change the contact</Action>
      <Action kind="delete" {...props}>Delete</Action>
    </div>
  </>
}

// One email of the shipment (US 168–169, ticket 27 Q11): the latest delivery to each role (the contact, the organizer), with its state and
// the time of it, and „Resend” for a message the shipment sends again now, while a recipient of now can be written to who is not being
// written to already.
function ShipmentEmail({ shipment, email, timezone, pending, onOpen }) {
  const name = messageName(email.message)
  const reached = email.deliveries.filter(delivery => delivery.email)
  return <div className="small mt-2" role="group" aria-label={name}>
    <div className="d-flex align-items-center flex-wrap">
      <i className="bx bx-envelope mr-1 text-muted" aria-hidden="true" /><strong className="mr-2">{name}</strong>
      {email.recipients && <Button color="link" size="sm" className="p-0 align-baseline" disabled={pending || !email.recipients.some(resendable)}
        onClick={() => onOpen("resend", shipment, { message: email.message })}>Resend</Button>}
    </div>
    {/* What asks for a resend stands out. */}
    {reached.length ? <ul className="list-unstyled mb-0">{reached.map(delivery => <li key={delivery.id}
      className={asksResend(delivery.status) ? `text-${emailColor(delivery.status)}` : ""}>
      {emailDeliveryLabel(delivery, timezone)}
    </li>)}</ul>
      : <p className="text-muted mb-0">{email.deliveries.length ? "Nobody had an email: nothing was sent." : "No delivery recorded."}</p>}
  </div>
}

// "CS Olimpia Brașov, Brașov · Andrei Popa": where a supplementary shipment goes and to whom.
const deliveryLabel = ({ address, contact }) => [address ? `${[address.recipient, address.locality].filter(Boolean).join(", ")}${address.confirmed ? ""
  : " (to confirm)"}` : "No address", contact?.name || "no contact"].join(" · ")

// Its state, as its badge says it: the main shipment's is its sheet's (`sheetStatus`), ready for processing from the sheet's validation
// until the warehouse processes it; a supplementary shipment's is its own.
const stateOf = (shipment, sheetStatus) => sheetStatuses[shipment.kind === "main" ? sheetStatus : shipment.status]
  || { label: shipment.status, color: "secondary" }
// Who shipped it and when: marked as shipped (D15), or validated in one step (before Validate sheet and the processing of supplementary
// shipments existed).
const shippedByLabel = (shipment, timezone) => (shipment.shippedBy ? `Marked as shipped by ${actorName(shipment.shippedBy)}, ${logTime(shipment.shippedAt, timezone)}`
  : `Validated by ${actorName(shipment.validatedBy)}, ${logTime(shipment.validatedAt, timezone)}`)
const processedByLabel = (shipment, timezone) => `Processed by ${actorName(shipment.processedBy)}, ${logTime(shipment.processedAt, timezone)}`

// One shipment: in preparation, what it sends and its last cancellation; once its travel is stated (the main shipment's sheet validated,
// a supplementary shipment processed), how it will travel; shipped, how and when it left; once processed, a Cargus AWB's label and the day
// the courier picks up one Cargus generated; what it sent and what came back; then each email it sent, the requirements and the AWB email of
// the main shipment included. A supplementary shipment also names its reason, and before it leaves where it goes.
function Shipment({ shipment, timezone, sheetStatus, readOnly, hasCourier, pending, onOpen, labelling, onLabel }) {
  const shipped = shipmentShipped(shipment.status)
  const ready = shipmentReadyToShip(shipment.status)
  const state = stateOf(shipment, sheetStatus)
  const supplementary = shipment.kind === "supplementary"
  const title = shipmentTitle(shipment)
  const carried = carriedItems(shipment)
  const emails = shipment.emails?.length ? shipment.emails : shipment.email ? [{ ...shipment.email, resendable: true }] : []
  return <div className="border rounded p-2 mb-2" role="group" aria-label={title}>
    <div className="d-flex justify-content-between flex-wrap">
      <div>
        <strong>{title}</strong>{" "}
        <Badge color={`soft-${state.color}`}>{state.label}</Badge>
        {shipped && <span className="small text-muted">{` ${dayLabel(shipment.date)}`}</span>}
      </div>
      {/* In preparation, the weight an administrator states after weighing it (US 96); processed, its parcels'. */}
      {!shipped && <span className="small text-muted">{shipmentWeightLabel(shipment)}
        {shipmentEditable(shipment.status) && !readOnly && <Button color="link" size="sm" className="p-0 ml-2 align-baseline" disabled={pending}
          onClick={() => onOpen("weight", shipment)}>Set weight</Button>}
      </span>}
    </div>
    {shipped && <div className="small mt-1">
      <span>{[transportLabel(shipment), shipmentWeightLabel(shipment)].filter(Boolean).join(" · ")}</span>
      {shipment.trackingUrl && <> · <a href={shipment.trackingUrl} target="_blank" rel="noopener noreferrer">Track</a></>}
      {/* A Cargus AWB's label, asked of Cargus at each download (US 106). */}
      {cargusLabelled(shipment) && <> · <Button color="link" size="sm" className="p-0 align-baseline"
        disabled={labelling || !hasCourier} onClick={() => onLabel(shipment)}>Download label</Button></>}
    </div>}
    {(shipped || ready) && shipment.pickupDay && <div className="small text-muted">{`Estimated pickup: ${dayLabel(shipment.pickupDay)}`}</div>}
    {/* The courier's last status (US 108–109) and its alerts as the sheet gives them (US 110); a server without a courier shows only the
      tracking link. A parcel returned to the sender asks for its return, recorded below. */}
    {shipped && hasCourier && shipment.courierStatus && <div className="small text-muted">{courierStatusLabel(shipment.courierStatus)}</div>}
    {shipped && hasCourier && shipment.courierAlerts?.map(alert => <div key={alert} className="small text-danger">{courierAlertHints[alert]}</div>)}
    <div className="small mt-1">{carried.length ? carried.join(" · ") : "Nothing from MPG"}</div>
    {supplementary && <div className="small text-muted">{`Reason: ${shipment.reason}`}</div>}
    {supplementary && !shipped && <div className="small text-muted">{`To: ${deliveryLabel(shipment)}`}</div>}
    {/* The main shipment of a validated sheet, as the sheet's validation and its processing stated it, or a supplementary shipment as its
      processing stated it: its transport, the AWB once given, its label once processed, the main shipment's content locked. */}
    {!shipped && (shipment.sheetValidatedAt || (supplementary && shipment.transport)) && <div className="small mt-1">
      <span>{transportLabel(shipment)}</span>
      {shipment.trackingUrl && <> · <a href={shipment.trackingUrl} target="_blank" rel="noopener noreferrer">Track</a></>}
      {cargusLabelled(shipment) && <> · <Button color="link" size="sm" className="p-0 align-baseline"
        disabled={labelling || !hasCourier} onClick={() => onLabel(shipment)}>Download label</Button></>}
    </div>}
    {shipped ? <div className="small text-muted">{shippedByLabel(shipment, timezone)}</div>
      : shipment.sheetValidatedAt ? <div className="small text-muted">
        {`Locked at the sheet's validation, validated by ${actorName(shipment.sheetValidatedBy)}, ${logTime(shipment.sheetValidatedAt, timezone)}`}
        {ready && <div>{processedByLabel(shipment, timezone)}</div>}</div>
        : supplementary ? ready && <div className="small text-muted">{processedByLabel(shipment, timezone)}</div>
          : <div className="small text-muted">Content = the sheet's lines with supplier MPG and an effective quantity above 0; it follows the sheet until the validation.</div>}
    {!shipped && shipment.heldAwb && <div className="small text-muted">{`AWB kept from the last validation: ${transportLabel({ transport: "courier",
      ...shipment.heldAwb })}`}</div>}
    {!shipped && shipment.cancellation && <div className="small text-warning mt-1">
      <i className="bx bx-undo mr-1" aria-hidden="true" />{cancellationLabel(shipment.cancellation, timezone)}
    </div>}
    {shipped && shipment.returns?.length > 0 && <ul className="small list-unstyled mt-1 mb-0">{shipment.returns.map((entry, index) => <li key={index}>
      <i className="bx bx-undo mr-1 text-warning" aria-hidden="true" />{returnLabel(entry, shipment, timezone)}
    </li>)}</ul>}
    {/* A canceled event's sheet sends no shipment email again (D10). */}
    {emails.map(email => <ShipmentEmail key={email.origin || email.message} shipment={shipment} email={email} timezone={timezone} pending={pending || readOnly}
      onOpen={onOpen} />)}
    {shipped && <ShippedActions shipment={shipment} readOnly={readOnly} pending={pending} onOpen={onOpen} />}
    {supplementary && !shipped && <PreparingActions shipment={shipment} readOnly={readOnly} hasCourier={hasCourier} pending={pending} onOpen={onOpen} />}
  </div>
}

// The sheet's shipments (CONTEXT „Expediere principală”, „Expediere suplimentară”): the main shipment in preparation or as it was
// validated, then corrected, with its returns; then the supplementary shipments, created while the main one is shipped. `onOpen(kind,
// shipment)` opens a dialog on a shipment, the main one when none is named; `onLabel(shipment)` downloads a Cargus AWB's label, one at a
// time (`labelling`). On a server without a courier (`hasCourier` false), the generation and the labels show disabled. A canceled event's
// sheet is read-only (D10): its shipments keep only their returns and the cancellation of an AWB that did not leave.
export default function SheetShipments({ sheet, timezone, hasCourier = true, pending, onOpen, labelling, onLabel }) {
  if (!sheet.shipments?.length) return null
  const readOnly = Boolean(sheet.eventCanceled)
  const mainShipped = sheetShipped(sheet.status) && !readOnly
  // What this section asks of Cargus: a Cargus AWB's label, or a supplementary shipment's AWB to a Romanian address.
  const cargus = sheet.shipments.some(shipment => cargusLabelled(shipment)
    || (shipment.kind === "supplementary" && shipmentEditable(shipment.status) && !abroad(shipment.address)))
  return <Card tag="section" id="logistics-shipments" aria-label="Shipments"><CardBody>
    <div className="d-flex justify-content-between align-items-start flex-wrap">
      <h4 className="card-title mb-1">Shipments</h4>
      {mainShipped && <Button color="primary" outline size="sm" className="mb-1" disabled={pending} onClick={() => onOpen("supplementary")}>
        <i className="bx bx-plus mr-1" aria-hidden="true" />Supplementary shipment
      </Button>}
    </div>
    <p className="text-muted small">One AWB per shipment. The stock decreases when a shipment leaves, not when the sheet is validated.
      Supplementary shipments do not change the sheet lines.</p>
    {!hasCourier && cargus && <p className="small text-muted">{`${COURIER_UNCONFIGURED}: AWBs are entered by hand, and no label can be downloaded.`}</p>}
    {sheet.shipments.map(shipment => <Shipment key={shipment.id || shipment.kind} shipment={shipment} timezone={timezone} sheetStatus={sheet.status}
      readOnly={readOnly} hasCourier={hasCourier} pending={pending} onOpen={onOpen} labelling={labelling} onLabel={onLabel} />)}
  </CardBody></Card>
}
